// Goals: every goal answers two questions, what counts (its measure) and what
// shape it takes. A habit repeats on a schedule and keeps a streak; a target
// is an amount to reach by a date and is paced. Writing goals are measured
// by the writing log on their own; check-in and count goals are logged by
// hand (see the goal log).

export type GoalMeasure = 'writing' | 'check' | 'count'
export type GoalShape = 'habit' | 'target'

export const GOAL_COLORS = ['accent', 'blue', 'success', 'warning', 'error', 'muted'] as const
type GoalColor = typeof GOAL_COLORS[number]

export type GoalSchedule =
  /** Any N days a week; rest days never break the streak. 7 is every day. */
  | { kind: 'weekly'; daysPerWeek: number }
  /** Particular weekdays (0 = Sunday); other days never break the streak. */
  | { kind: 'weekdays'; days: number[] }

export interface Goal {
  id: string
  title: string
  measure: GoalMeasure
  shape: GoalShape
  /** Writing habit: net words a day (0 ignores words). Writing target: total words in scope. */
  words: number
  /** Writing habit: active minutes a day (0 ignores time). */
  minutes: number
  /** Writing habit: finishing an Editor Mode revision pass also counts. */
  passes: boolean
  /** Writing goals: the desk or folder that counts; null for the whole corpus. */
  scopeId: string | null
  /** Count habit: amount a day. Check-in or count target: the total to reach. */
  amount: number
  /** Count goals: what is counted, e.g. "glasses". */
  unit: string
  schedule: GoalSchedule
  /** Check-in and count targets count from this day. 'YYYY-MM-DD'. */
  startsOn: string
  /** Targets: 'YYYY-MM-DD'. */
  deadline: string | null
  /** Targets: weekdays (0 = Sunday) with nothing planned. */
  restDays: number[]
  color: GoalColor
  /** An icon from the panel icon catalog, or null. */
  icon: string | null
  /** Position in the list; lower first. */
  order: number
  archived: boolean
  /** The writing habit shown in the bottom bar and on the calendar. */
  featured: boolean
  /** The day the goal was set. */
  createdOn: string
}

export interface GoalDays {
  /** 0 = Sunday, 1 = Monday, 6 = Saturday. */
  weekStartsOn: 0 | 1 | 6
  /** Hour (0–6) at which a new day begins, for people who work past midnight. */
  dayStartHour: number
}

export const DEFAULT_GOAL_DAYS: GoalDays = { weekStartsOn: 1, dayStartHour: 0 }

const DAY = /^\d{4}-\d{2}-\d{2}$/

function whole(value: unknown, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(value))
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback
}

function weekdays(raw: unknown): number[] {
  return Array.isArray(raw)
    ? [...new Set<number>(raw.filter((d): d is number => Number.isInteger(d) && d >= 0 && d <= 6))].sort()
    : []
}

export function normalizeGoalDays(raw: any): GoalDays {
  return {
    weekStartsOn: raw?.weekStartsOn === 0 || raw?.weekStartsOn === 6 ? raw.weekStartsOn : 1,
    dayStartHour: whole(raw?.dayStartHour, 0, 6, DEFAULT_GOAL_DAYS.dayStartHour),
  }
}

function normalizeSchedule(raw: any): GoalSchedule {
  if (raw?.kind === 'weekdays') {
    const days = weekdays(raw.days)
    if (days.length) return { kind: 'weekdays', days }
  }
  return { kind: 'weekly', daysPerWeek: whole(raw?.daysPerWeek, 1, 7, 7) }
}

/** Validates a goal read from storage or the cloud; null rejects it. */
export function parseGoal(raw: any): Goal | null {
  if (!raw || typeof raw.id !== 'string' || !raw.id) return null
  const measure: GoalMeasure = ['writing', 'check', 'count'].includes(raw.measure) ? raw.measure : 'check'
  const shape: GoalShape = raw.shape === 'target' ? 'target' : 'habit'
  const createdOn = DAY.test(raw.createdOn) ? raw.createdOn : '1970-01-01'
  const goal: Goal = {
    id: raw.id,
    title: typeof raw.title === 'string' ? raw.title.trim().slice(0, 200) : '',
    measure,
    shape,
    words: whole(raw.words, 0, 10_000_000, 0),
    minutes: whole(raw.minutes, 0, 1_440, 0),
    passes: raw.passes === true,
    scopeId: measure === 'writing' && typeof raw.scopeId === 'string' ? raw.scopeId : null,
    amount: whole(raw.amount, 0, 1_000_000, 0),
    unit: typeof raw.unit === 'string' ? raw.unit.trim().slice(0, 40) : '',
    schedule: normalizeSchedule(raw.schedule),
    startsOn: DAY.test(raw.startsOn) ? raw.startsOn : createdOn,
    deadline: DAY.test(raw.deadline) ? raw.deadline : null,
    restDays: weekdays(raw.restDays),
    color: GOAL_COLORS.includes(raw.color) ? raw.color : 'accent',
    icon: typeof raw.icon === 'string' && raw.icon ? raw.icon : null,
    order: Number.isFinite(raw.order) ? raw.order : 0,
    archived: raw.archived === true,
    featured: measure === 'writing' && shape === 'habit' && raw.featured === true,
    createdOn,
  }
  return isComplete(goal) ? goal : null
}

/** Whether a goal has everything its measure and shape need. */
export function isComplete(goal: Goal): boolean {
  if (goal.shape === 'target') {
    if (!goal.deadline) return false
    if (goal.measure === 'writing') return goal.words > 0
    return goal.amount > 0
  }
  if (goal.measure === 'writing') return goal.words > 0 || goal.minutes > 0 || goal.passes
  if (goal.measure === 'count') return goal.amount > 0
  return true
}

/** A fresh goal of the given measure and shape, with sensible defaults. */
export function blankGoal(id: string, today: string, measure: GoalMeasure = 'check', shape: GoalShape = 'habit'): Goal {
  return {
    id,
    title: '',
    measure,
    shape,
    words: measure === 'writing' ? (shape === 'target' ? 80_000 : 500) : 0,
    minutes: measure === 'writing' && shape === 'habit' ? 30 : 0,
    passes: measure === 'writing' && shape === 'habit',
    scopeId: null,
    amount: measure === 'count' ? (shape === 'target' ? 20 : 8) : measure === 'check' && shape === 'target' ? 30 : 0,
    unit: '',
    schedule: { kind: 'weekly', daysPerWeek: measure === 'writing' ? 5 : 7 },
    startsOn: today,
    deadline: null,
    restDays: [],
    color: 'accent',
    icon: null,
    order: 0,
    archived: false,
    featured: false,
    createdOn: today,
  }
}

/** What a new goal starts from, as set in Settings > Goals & Habits. */
export interface NewGoalDefaults {
  words: number
  minutes: number
  writingDays: number
  otherDays: number
  color: Goal['color']
  restDays: number[]
}

/** A fresh goal with the writer's own defaults laid over the built-in ones. */
export function goalWithDefaults(goal: Goal, defaults: NewGoalDefaults): Goal {
  const next: Goal = { ...goal, color: defaults.color }
  if (goal.shape === 'habit') {
    next.schedule = { kind: 'weekly', daysPerWeek: goal.measure === 'writing' ? defaults.writingDays : defaults.otherDays }
    if (goal.measure === 'writing') {
      next.words = defaults.words
      next.minutes = defaults.minutes
    }
  } else if (defaults.restDays.length < 7) {
    next.restDays = [...defaults.restDays]
  }
  return next
}

/** The name a goal shows: its title, or one made from what it measures. */
export function goalName(goal: Goal, scopeName: (id: string | null) => string): string {
  if (goal.title) return goal.title
  if (goal.measure === 'writing') return goal.scopeId || goal.shape === 'target' ? scopeName(goal.scopeId) : 'Writing'
  return goal.measure === 'count' && goal.unit ? goal.unit : 'Untitled goal'
}

// ── From the earlier, writing-only goals ───────────────────────────────────

/** The daily writing habit as it was stored before goals had measures. */
export interface LegacyWritingHabit {
  enabled: boolean
  dailyWords: number
  dailyMinutes: number
  daysPerWeek: number
  weekStartsOn: 0 | 1
  dayStartHour: number
  passDays: boolean
}

/** The fixed id the old writing habit takes, so devices that migrate at once agree. */
export const WRITING_HABIT_ID = 'writing-habit'

export function goalFromWritingHabit(habit: LegacyWritingHabit, today: string): Goal {
  return {
    ...blankGoal(WRITING_HABIT_ID, today, 'writing', 'habit'),
    title: '',
    words: Math.max(0, Math.round(habit.dailyWords)),
    minutes: Math.max(0, Math.round(habit.dailyMinutes)),
    passes: habit.passDays,
    schedule: { kind: 'weekly', daysPerWeek: whole(habit.daysPerWeek, 1, 7, 5) },
    featured: true,
    order: -1,
  }
}

/** A project target from before goals had measures; it keeps its id. */
export function goalFromProject(raw: any, order: number): Goal | null {
  if (!raw || typeof raw.id !== 'string' || !DAY.test(raw.deadline) || !(raw.target > 0)) return null
  return parseGoal({
    ...blankGoal(raw.id, DAY.test(raw.createdOn) ? raw.createdOn : raw.deadline, 'writing', 'target'),
    scopeId: typeof raw.scopeId === 'string' ? raw.scopeId : null,
    words: Math.round(raw.target),
    deadline: raw.deadline,
    restDays: weekdays(raw.restDays),
    order,
  })
}
